/**
 * Quiet feedback (TMMPDM-0034, FEED-004 / NOTIF-003 / NOTIF-004): one Snackbar for success that
 * needs no action — "Tenant created.", "Price added.", "Copied". Anything the user must act on (a
 * follow-up, a warning, an error) stays an inline `Alert` banner in the screen.
 *
 * Bottom-left, clear of the navigation rail (`inset`), so it never covers a dialog's or a page's
 * primary actions on the right; auto-hides after 5 s; `role="status"` so it is announced politely;
 * a close button. A click elsewhere does not dismiss it.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { IconButton, Snackbar, type SnackbarCloseReason } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCopy } from '@/hooks/useCopy';

export const NOTIFY_AUTO_HIDE_MS = 5000;

export interface Notify {
  success: (text: string) => void;
  info: (text: string) => void;
}

interface Toast {
  id: number;
  text: string;
}

const NotifyContext = createContext<Notify | null>(null);

interface NotifyProviderProps {
  children: ReactNode;
  /** Width (px) of the fixed navigation on md+, so the toast sits beside it rather than over it. */
  inset?: number;
}

export function NotifyProvider({ children, inset = 0 }: NotifyProviderProps) {
  const { t } = useCopy();
  const [toast, setToast] = useState<Toast | null>(null);
  const [open, setOpen] = useState(false);

  const show = useCallback((text: string) => {
    setToast((current) => ({ id: (current?.id ?? 0) + 1, text }));
    setOpen(true);
  }, []);

  const notify = useMemo<Notify>(() => ({ success: show, info: show }), [show]);

  const handleClose = (_event: unknown, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const closeLabel = t('common.close', 'Close');

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <Snackbar
        key={toast?.id}
        open={open}
        autoHideDuration={NOTIFY_AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={toast?.text}
        slotProps={{ content: { role: 'status' } }}
        sx={{ left: { md: inset + 24 } }}
        action={
          <IconButton size="small" color="inherit" aria-label={closeLabel} onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </NotifyContext.Provider>
  );
}

/** The quiet-success channel. Throws outside `NotifyProvider` — a missing provider is a bug. */
export function useNotify(): Notify {
  const notify = useContext(NotifyContext);
  if (!notify) throw new Error('useNotify must be used inside NotifyProvider');
  return notify;
}
